/**
 * PWA push notification subscription helper.
 */

/**
 * Convert base64 VAPID public key to Uint8Array for PushManager.
 */
function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const rawData = atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

/**
 * Check whether a push subscription endpoint has been invalidated by the
 * browser (e.g. after clearing site data or after the subscription expires).
 */
function isInvalidEndpoint(endpoint: string): boolean {
  return !endpoint || endpoint.includes("permanently-removed.invalid");
}

/**
 * Register service worker and subscribe to push notifications.
 * Returns the PushSubscription object or null on failure.
 */
export async function subscribeToPush(
  vapidPublicKey: string,
): Promise<PushSubscription | null> {
  try {
    // Check if service workers and push notifications are supported
    if (!("serviceWorker" in navigator) || !("PushManager" in window)) {
      return null;
    }

    // Check notification permission
    if (Notification.permission === "denied") {
      return null;
    }

    // Request permission if not already granted
    if (Notification.permission === "default") {
      const permission = await Notification.requestPermission();
      if (permission !== "granted") {
        return null;
      }
    }

    // Ensure service worker is registered and ready
    let registration = await navigator.serviceWorker.getRegistration();
    if (!registration) {
      registration = await navigator.serviceWorker.register("/sw.js");
    }
    // Wait until the SW is active — avoids "Registration failed" errors
    registration = await navigator.serviceWorker.ready;

    // Reuse an existing subscription if one already exists
    const existing = await registration.pushManager.getSubscription();
    if (existing) {
      // If the browser invalidated the endpoint, discard and re-create
      if (isInvalidEndpoint(existing.endpoint)) {
        await existing.unsubscribe();
      } else {
        return existing;
      }
    }

    // Subscribe to push
    const applicationServerKey = urlBase64ToUint8Array(vapidPublicKey);
    const subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: applicationServerKey as BufferSource,
    });

    // Some browsers (e.g. Edge on Android) produce an invalid endpoint when
    // their push state is corrupted.  Nothing we can do — return null so the
    // caller knows push is unavailable without disrupting the service worker.
    if (isInvalidEndpoint(subscription.endpoint)) {
      console.warn("Push subscription has invalid endpoint — push unavailable on this browser");
      await subscription.unsubscribe().catch(() => {});
      return null;
    }

    return subscription;
  } catch (err) {
    console.error("Push subscription failed:", err);
    return null;
  }
}

/**
 * Force re-register push notifications by unsubscribing the existing
 * subscription and creating a fresh one.
 */
export async function resubscribeToPush(
  vapidPublicKey: string,
): Promise<PushSubscription | null> {
  try {
    if (!("serviceWorker" in navigator) || !("PushManager" in window)) {
      return null;
    }

    if (Notification.permission === "denied") {
      return null;
    }

    if (Notification.permission === "default") {
      const permission = await Notification.requestPermission();
      if (permission !== "granted") {
        return null;
      }
    }

    let registration = await navigator.serviceWorker.getRegistration();
    if (!registration) {
      registration = await navigator.serviceWorker.register("/sw.js");
    }
    registration = await navigator.serviceWorker.ready;

    // Unsubscribe existing subscription
    const existing = await registration.pushManager.getSubscription();
    if (existing) {
      await existing.unsubscribe();
    }

    // Create a fresh subscription
    const applicationServerKey = urlBase64ToUint8Array(vapidPublicKey);
    const subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: applicationServerKey as BufferSource,
    });

    if (isInvalidEndpoint(subscription.endpoint)) {
      console.warn("Push subscription has invalid endpoint — push unavailable on this browser");
      await subscription.unsubscribe().catch(() => {});
      return null;
    }

    return subscription;
  } catch (err) {
    console.error("Push re-subscription failed:", err);
    return null;
  }
}

/**
 * Send push subscription to the server.
 */
export async function sendSubscriptionToServer(
  subscription: PushSubscription,
  ownerType: "admin" | "client",
  ownerKey: string,
  headers: Record<string, string>,
): Promise<boolean> {
  try {
    // Never send an invalidated subscription to the server
    if (isInvalidEndpoint(subscription.endpoint)) {
      console.warn("Skipping push subscribe — endpoint is invalidated");
      return false;
    }

    const subJSON = subscription.toJSON();
    const response = await fetch("/api/push/subscribe", {
      method: "POST",
      headers,
      body: JSON.stringify({
        endpoint: subscription.endpoint,
        p256dh: subJSON.keys?.p256dh,
        auth: subJSON.keys?.auth,
        owner_type: ownerType,
        owner_key: ownerKey,
      }),
    });

    return response.ok;
  } catch (err) {
    console.error("Failed to send subscription to server:", err);
    return false;
  }
}
